/* eslint-disable max-lines */
import Button from '@components/atoms/ui-kit/button.tsx';
import { useMemo, useState } from 'react';
import PageHeader from '@components/layout/page-header';
import { useTranslation } from 'react-i18next';
import CreateLanguageAndConfig from '@components/back-office/language-and-configs/create-language-and-config';
import { Plus, Eye } from 'lucide-react';
import { useGetModels } from '@utils/api/models/use-get-models';
import { PATHS } from '../../router/paths';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';

import { type LanguageAndConfigPayload } from '@utils/api/back-office/language-and-config/use-create-language-and-config';
import {
  useLanguageAndConfig,
  type LanguageConfiguration
} from '@utils/api/back-office/language-and-config/use-get-languages-and-config';

type Row = {
  id: number;
  bcp47: string;
  key: string;
  name: string;
  type: string; // "Base" | "Custom"
  is_private: boolean;
  is_enabled: boolean;
};

export default function LanguageAndConfig() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data } = useLanguageAndConfig();
  const { data: supportedLanguages } = useGetModels();

  const schema = Yup.object().shape({
    id: Yup.number(),
    bcp47: Yup.string().required(t('bcp47-value-is-required')),
    key: Yup.string()
      .max(6, t('string-should-have-at-most-6-characters'))
      .required(t('key-value-is-required')),
    name: Yup.string().required(t('name-value-is-required')),
    is_private: Yup.boolean(),
    is_enabled: Yup.boolean(),
    asr_url: Yup.string().optional(),
    asr_model_location: Yup.string().optional(),
    asr_audio_rate: Yup.number()
      .transform((v, o) => (o === '' ? undefined : v))
      .typeError(t('asr-audio-rate-must-be-a-number'))
      .optional(),
    punctuation_url: Yup.string().optional(),
    punctuation_model_location: Yup.string().optional(),
    asr_version: Yup.string()
      .max(6, t('string-should-have-at-most-5-characters'))
      .optional(),
    asr_common_version: Yup.string().optional(),
    alignment_language_code: Yup.string().optional(),
    alignment_version: Yup.string().optional(),
    deepl_code: Yup.string().optional()
  });

  const {
    control,
    handleSubmit,
    reset,
    register,
    formState: { errors }
  } = useForm<LanguageAndConfigPayload>({
    resolver: yupResolver(schema)
  });

  // helpers to map "key"/bcp to display name from supportedLanguages
  const norm = (s?: string) => (s ?? '').toLowerCase().trim();
  const list = supportedLanguages?.result ?? [];
  const knownKeys = new Set(list.map(l => norm(l.key)));
  const extractKey = (raw?: string) => {
    if (!raw) return undefined;
    const tokens = raw
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter(Boolean);
    const hit = tokens.find(t => knownKeys.has(t));
    if (hit) return hit;
    return tokens.find(t => t.length === 2 || t.length === 3);
  };
  const displayNameForKey = (rawKey?: string, bcp47?: string) => {
    const k1 = extractKey(rawKey);
    if (k1) {
      const byKey = list.find(l => norm(l.key) === k1);
      if (byKey?.name) return byKey.name;
    }
    const langPart = norm(bcp47?.split('-')[0]);
    if (langPart) {
      const byBcp = list.find(l => norm(l.key) === langPart);
      if (byBcp?.name) return byBcp.name;
    }
    if (k1) return k1;
    return rawKey ?? '-';
  };

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    return data.result.map((item: LanguageConfiguration) => ({
      id: item.id,
      bcp47: item.bcp47,
      key: item.key,
      name: item.name,
      type: item.type === 1 ? 'Base' : 'Custom',
      is_private: item.is_private,
      is_enabled: item.is_enabled
    }));
  }, [data]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        id: 'id',
        header: t('id'),
        field: 'id',
        accessorKey: 'id',
        maxWidth: 100,
        minWidth: 80,
        enableSorting: false,
        enableFiltering: false,
        cursor: 'pointer',
        filterVariant: 'number',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'name',
        header: t('name'),
        field: 'name',
        accessorKey: 'name',
        minWidth: 240,
        enableSorting: false,
        enableFiltering: false,
        cursor: 'pointer',
        filterVariant: 'text',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'bcp47',
        header: 'BCP-47',
        field: 'bcp47',
        accessorKey: 'bcp47',
        minWidth: 200,
        enableSorting: false,
        enableFiltering: false,
        cursor: 'pointer',
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '').toLowerCase()
      },
      {
        id: 'key',
        header: t('key'),
        field: 'key',
        accessorKey: 'key',
        minWidth: 160,
        enableSorting: true,
        enableFiltering: true,
        cursor: 'pointer',
        filterVariant: 'text',
        cell: ({ row }) =>
          displayNameForKey(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (row as any).original.key,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (row as any).original.bcp47
          )
      },
      {
        id: 'is_private',
        header: t('is-private'),
        field: 'is_private',
        accessorKey: 'is_private',
        maxWidth: 130,
        enableSorting: false,
        enableFiltering: false,
        cursor: 'pointer',
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => (getValue() ? t('yes') : t('no'))
      },
      {
        id: 'is_enabled',
        header: t('is-enabled'),
        field: 'is_enabled',
        accessorKey: 'is_enabled',
        maxWidth: 130,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        cursor: 'pointer',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => (getValue() ? t('yes') : t('no'))
      },
      {
        id: 'type',
        header: t('type'),
        field: 'type',
        accessorKey: 'type',
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cursor: 'pointer',
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('base'), value: 'Base' },
          { label: t('custom'), value: 'Custom' }
        ],
        cell: ({ getValue }) => {
          const v = String(getValue() ?? '');
          return v === 'Base' ? t('base') : v === 'Custom' ? t('custom') : v;
        }
      }
    ];
  }, [t, supportedLanguages]);

  const handleCellClick = (row: Row, field: string) => {
    if (!row.id) return;
    if (['id', 'name', 'bcp47', 'key'].includes(field)) {
      navigate(PATHS(String(row.id)).LANGUAGE_AND_PUBLICATIONS);
    }
  };

  // Actions for mobile & desktop
  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'view',
        label: t('view'),
        icon: <Eye className="h-4 w-4" />,
        intent: 'primary',
        run: (row: Row) => {
          navigate(PATHS(String(row.id)).LANGUAGE_AND_PUBLICATIONS);
        }
      }
    ],
    [t, navigate]
  );

  return (
    <div className="py-2 px-4">
      <CreateLanguageAndConfig
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        reset={reset}
        handleSubmit={handleSubmit}
        control={control}
        errors={errors}
        register={register}
      />

      <PageHeader
        title={t('language-and-configs')}
        caption={t('view-and-update-languages-and-configurations-below')}
      >
        <Button
          onClick={() => {
            reset({
              id: undefined,
              bcp47: '',
              key: '',
              name: '',
              is_private: false,
              is_enabled: false,
              asr_url: '',
              asr_model_location: '',
              asr_audio_rate: undefined,
              punctuation_url: '',
              punctuation_model_location: '',
              asr_version: '',
              asr_common_version: '',
              alignment_language_code: '',
              alignment_version: '',
              deepl_code: ''
            });
            setIsModalOpen(true);
          }}
          label={t('create-language-config')}
          icon={<Plus width={14} />}
        />
      </PageHeader>

      <div className="w-full p-2 md:p-0">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={data?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="88vh"
          dense
          grid={false}
          striped
          loading={!data}
          getRowId={row => String(row.id)}
          onCellClick={handleCellClick}
        />
      </div>
    </div>
  );
}
